import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryTabs from '@/components/feature/CategoryTabs';
import Breadcrumb from '@/components/feature/Breadcrumb';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const categoryArticles = useMemo(
    () => (slug ? articles.filter((a) => a.categorySlug === slug) : articles),
    [slug]
  );

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '分类', href: '/category/beginner-guide' },
    ...(currentCategory ? [{ label: currentCategory.name }] : []),
  ];

  return (
    <div className="min-h-screen pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "url": `https://example.com/category/${slug || ''}`,
            "name": currentCategory ? `${currentCategory.name} - 黄油游戏官方指南` : '黄油游戏文章列表',
            "description": currentCategory?.description || '浏览所有黄油游戏文章',
            "isPartOf": {
              "@type": "WebSite",
              "name": "黄油游戏官方指南",
              "url": "https://example.com/"
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": categoryArticles.map((article, idx) => ({
                "@type": "ListItem",
                "position": idx + 1,
                "url": `https://example.com/article/${article.slug}`,
                "name": article.title,
              })),
            },
          }),
        }}
      />

      <Breadcrumb items={breadcrumbItems} />

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                {currentCategory ? currentCategory.name : '全部黄油游戏文章'}
              </h1>
              <p className="text-sm text-foreground-500 mt-1.5">
                {currentCategory
                  ? currentCategory.description
                  : '浏览所有分类的黄油游戏攻略与推荐文章'}
              </p>
            </div>
            <div className="text-sm text-foreground-400 whitespace-nowrap">
              共 {categoryArticles.length} 篇文章
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <CategoryTabs />
          </div>

          {currentCategory && (
            <div className="relative w-full aspect-[21/9] rounded-lg overflow-hidden mb-6 md:mb-8">
              <img
                src={currentCategory.image}
                alt={currentCategory.name}
                className="w-full h-full object-cover object-top"
                width="1200"
                height="514"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
          )}

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop="">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                <i className="ri-inbox-line text-4xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
              <Link
                to="/"
                className="inline-block mt-4 px-5 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}